import type { PropType } from 'vue';
import type {
  IdentifiedTab,
  TTabBarExtendedProps,
} from './TTabBarExtended.types';
import { computed, defineComponent, provide, ref } from 'vue';
import { TDropdown, TDropdownItem } from '../TDropdown';
import { TTab } from './TTab';
import { useStyles as useTTabStyles } from './TTab/TTab.styles';
import { useStyles } from './TTabBarExtended.styles';

export const TTabBarExtended = defineComponent({
  name: 'TTabBarExtended',
  emits: ['select'],
  props: {
    tabs: {
      type: Array as PropType<TTabBarExtendedProps['tabs']>,
      required: true,
    },
    maxTabs: {
      type: Number as PropType<TTabBarExtendedProps['maxTabs']>,
    },
    initialLabel: {
      type: String as PropType<TTabBarExtendedProps['initialLabel']>,
    },
    selectedValue: {
      type: [String, Number, Object] as PropType<TTabBarExtendedProps['selectedValue']>,
    },
  },
  setup(props, { emit }) {
    const baseClass = useStyles();
    const tTabBaseClass = useTTabStyles();

    const identifiedTabs = computed(() => props.tabs.map((tab, index): IdentifiedTab => ({
      id: `${index}`,
      label: tab.label,
      value: tab.value,
    })));

    const initialTab = computed(() => {
      if (!props.initialLabel) {
        return identifiedTabs.value[0];
      }

      return (
        identifiedTabs.value.find((tab) => tab.label === props.initialLabel)
        ?? identifiedTabs.value[0]
      );
    });
    const selectedTab = ref(initialTab.value);

    const isControlled = computed(() => props.selectedValue !== undefined);
    const effectiveSelectedTab = computed(() => {
      if (isControlled.value) {
        const found = identifiedTabs.value.find((tab) => tab.value === props.selectedValue);
        return found ?? identifiedTabs.value[0];
      }
      return selectedTab.value;
    });

    const isDropdownOpen = ref(false);

    const selectTab = (index: string) => {
      const newSelectedTab = identifiedTabs.value.find((tab) => tab.id === index);
      if (!newSelectedTab) {
        throw new Error('No tab was found.');
      }
      if (effectiveSelectedTab.value.id === index) {
        return;
      }

      if (!isControlled.value) {
        selectedTab.value = newSelectedTab;
      }

      emit('select', newSelectedTab.value);
    };
    provide('tabSelection', { selectTab });

    const amountOfTabs = computed(() => identifiedTabs.value.length);
    const maxAmountOfTabs = computed(() => Math.min(amountOfTabs.value, props.maxTabs || Infinity));
    const visibleTabs = computed(() => {
      if (amountOfTabs.value === maxAmountOfTabs.value) {
        return identifiedTabs.value;
      }

      const tabs = identifiedTabs.value.slice(0, maxAmountOfTabs.value - 1);
      const selectedTabIsAlreadyVisible = tabs.some(({ id }) => id === effectiveSelectedTab.value.id);
      if (selectedTabIsAlreadyVisible) {
        tabs.push(identifiedTabs.value[maxAmountOfTabs.value - 1]);
      } else {
        tabs.push(effectiveSelectedTab.value);
      }

      return tabs;
    });

    const shouldDropdownExist = computed(() => visibleTabs.value.length < identifiedTabs.value.length);

    const toggleIsDropdownOpen = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };
    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };

    const handleClickInDropdownTrigger = () => {
      toggleIsDropdownOpen();
    };
    const handleOnKeyPressInDropdownTrigger = (event: KeyboardEvent) => {
      event.preventDefault();

      if (event.key === 'Enter') {
        toggleIsDropdownOpen();
      }
    };

    const handleClickInDropdownItem = (index: string) => {
      selectTab(index);
      closeDropdown();
    };

    const Dropdown = () => (
      <TDropdown
        open={isDropdownOpen.value}
        position="center"
        onCancel={toggleIsDropdownOpen}
      >
        {{
          trigger: () => (
            <div
              class={[
                tTabBaseClass.value,
                `${tTabBaseClass.value}__dropdown-trigger`,
              ]}
              onClick={handleClickInDropdownTrigger}
              onKeypress={handleOnKeyPressInDropdownTrigger}
              tabindex={0}
              role="button"
            >
              . . .
            </div>
          ),
          default: () => identifiedTabs.value.map((tab) => (
            <TDropdownItem
              disabled={effectiveSelectedTab.value.id === tab.id}
              onClick={() => handleClickInDropdownItem(tab.id)}
            >
              {tab.label}
            </TDropdownItem>
          )),
        }}
      </TDropdown>
    );

    return () => (
      <nav role="navigation" class={[baseClass.value]}>
        {visibleTabs.value.map((tab) => (
          <TTab
            key={`${tab.label}-${tab.id}`}
            id={tab.id}
            selected={effectiveSelectedTab.value.id === tab.id}
            value={tab.value}
          >
            {tab.label}
          </TTab>
        ))}
        {shouldDropdownExist.value ? <Dropdown /> : null}
      </nav>
    );
  },
});
